import {
  isFunction as _isFunction,
  map as _map,
  get as _get,
  isNil as _isNil,
  filter as _filter,
} from 'lodash';

import { DXL } from '@vfgroup-oneplatform/foundation/Network';
import { retrieveUtilities } from '@vfgroup-oneplatform/foundation/Utils/ServicesUtils/Utilities';
import { findAndGet } from '@vfgroup-oneplatform/framework/Services/Utils/Utils';

import { API_URLS, EXTRAS_OPTION_CONFIG } from '../../Config';
import { PRODUCT_STATUS } from '../../Enums';

import { OCCURRENCE_TYPES } from './Enums';
import {
  getFormattedDate,
  getContractDataLabel,
  mapUsageData,
  mapUsers,
  mapUnassignedUsers,
} from './Utils';

const getPlansExtrasOption = (usageType, priceType) => {
  return {
    ...EXTRAS_OPTION_CONFIG[usageType][priceType],
    items: [],
  };
};
export const mapPlansResponse = (response, iconsMapper, contractDataLabelsMapper) => {
  const { product, usageConsumptionReport } = response;
  const filteredPlans = _filter(product, p =>
    [PRODUCT_STATUS.ACTIVE, PRODUCT_STATUS.TERMINATED].includes(p.status),
  );
  const mappedResponse = _map(filteredPlans, p => {
    const plan = {};
    plan.id = p.id;
    const type = findAndGet(p.productCharacteristic, 'name', 'plan-type', 'value');
    plan.type = type;
    const serviceType = findAndGet(p.productCharacteristic, 'name', 'service-plan-type', 'value');
    plan.serviceType = serviceType;
    plan.name = p.name;
    plan.status = p.status;
    plan.users = mapUsers(p);
    plan.unassignedUsers = mapUnassignedUsers(p);
    const productTerm = p.productTerm;
    if (productTerm) {
      plan.contractData = _map(productTerm, term => {
        const { name, validFor } = term;
        return {
          key: _isFunction(contractDataLabelsMapper)
            ? contractDataLabelsMapper(name)
            : getContractDataLabel(name),
          value: getFormattedDate(validFor),
          type: name,
        };
      });
    }
    const price = _get(p, 'productPrice[0].price.taxIncludedAmount.value');
    plan.price = !_isNil(price) ? parseFloat(price).toFixed(2) : null;
    plan.currency = _get(p, 'productPrice[0].price.taxIncludedAmount.unit');
    plan.usageData = mapUsageData(p, usageConsumptionReport, iconsMapper);
    return plan;
  });
  return mappedResponse;
};

export const mapPlansExtrasResponse = (response, isCurrencyFirst = false) => {
  const mappedResponse = {};
  const plan = response[0];
  const extrasData = plan.productOfferingRelationship[0].productOffering;
  extrasData.forEach(({ description, productOfferingPrice, category }) => {
    const {
        price: { unit, value },
        priceType,
      } = productOfferingPrice[0],
      usageType = category[0].name.toLocaleLowerCase(),
      optionTypeIndex = OCCURRENCE_TYPES.RECURRING === priceType ? 1 : 0;

    if (!mappedResponse[usageType]) {
      mappedResponse[usageType] = {
        options: [
          getPlansExtrasOption(usageType, OCCURRENCE_TYPES.ONE_OFF),
          getPlansExtrasOption(usageType, OCCURRENCE_TYPES.RECURRING),
        ],
      };
    }

    const priceValue = isCurrencyFirst ? `${unit}${value}` : `${value}${unit}`;
    mappedResponse[usageType].options[optionTypeIndex].items.push({
      title: `${description}`,
      subTitle: `${priceValue}`,
    });
  });

  return mappedResponse;
};

export async function getPlansData(organizationId, customHeaders = {}, utilities = {}) {
  const _utilities = retrieveUtilities(utilities, {
    responseMapper: mapPlansResponse,
  });

  const {
    errorMapper,
    onError,
    onStart,
    onSuccess,
    responseMapper,
    iconsMapper,
    contractDataLabelsMapper,
  } = _utilities;

  const headers = {
    'vf-project': 'SoHo',
    Accept: 'application/json',
    'vf-target-environment': 'soho-eu9-test',
    ...customHeaders,
  };
  const data = {
    params: {
      "relatedParty[(@referredType=='organization')].id": organizationId,
      '@type': 'Plan',
    },
  };

  onStart();
  try {
    const response = await DXL.request('GET', API_URLS.ActiveProductsStatus, headers, data);
    const _response = responseMapper(response.data, iconsMapper, contractDataLabelsMapper);
    onSuccess(_response);
    return _response;
  } catch (error) {
    const _error = errorMapper(error);
    if (_isFunction(onError)) {
      onError(_error);
    } else {
      throw _error;
    }
  }
}

export async function getPlansExtrasData(planId, customHeaders = {}, utilities = {}) {
  const _utilities = retrieveUtilities(utilities, {
    responseMapper: mapPlansExtrasResponse,
  });

  const { errorMapper, onError, onStart, onSuccess, responseMapper } = _utilities;

  // TODO: add Content-Type : 'application/json'
  const headers = {
    'vf-project': 'SoHo',
    Accept: 'application/json',
    'vf-target-environment': 'Test',
    ...customHeaders,
  };
  const data = {
    params: {
      'productOfferingRelationship.relationshipType': 'Addon',
      id: planId,
    },
  };

  onStart();
  try {
    const response = await DXL.request('GET', API_URLS.ProductOffering, headers, data);
    const _response = responseMapper(response.data);
    onSuccess(_response);
    return _response;
  } catch (error) {
    const _error = errorMapper(error);
    if (_isFunction(onError)) {
      onError(_error);
    } else {
      throw _error;
    }
  }
}

export async function removeUserFromPlan(planId, userId, customHeaders = {}) {
  const headers = {
    'vf-project': 'SoHo',
    Accept: 'application/json',
    'vf-target-environment': 'soho-eu9-test',
    ...customHeaders,
  };
  const options = {
    data: [
      {
        op: 'remove',
        path: `/relatedParty.id=${userId}`,
      },
    ],
  };

  return DXL.request('PATCH', `${API_URLS.Product}/${planId}`, headers, options);
}

export async function addUsersToPlan(planId, users, customHeaders = {}) {
  const headers = {
    'vf-project': 'SoHo',
    Accept: 'application/json',
    'vf-target-environment': 'soho-eu9-test',
    ...customHeaders,
  };

  const data = _map(users, user => {
    return {
      op: 'add',
      path: '/relatedParty',
      value: user,
    };
  });

  const options = {
    data,
  };

  return DXL.request('PATCH', `${API_URLS.Product}/${planId}`, headers, options);
}
